import React, {useEffect, useState} from "react";
import {toast} from "react-toastify";
import {Box, Button, Column, Columns, Input, Modal, Section, Title} from "@allxsmith/bestax-bulma";
import {HELPER_DOWNLOAD, getSitePrinter, helperStatus, scanForPrinters, setSitePrinter, testPrint} from "./labelPrinter";

// Find the network Zebra from this PC and make it the printer for the whole site.
export default function LabelPrinterModal({open, setOpen}) {
    const [printer, setPrinter] = useState(null);
    const [helper, setHelper] = useState(undefined);  // undefined = checking, null = not running
    const [scanning, setScanning] = useState(false);
    const [found, setFound] = useState(null);
    const [networks, setNetworks] = useState([]);
    const [manualIp, setManualIp] = useState("");
    const [busy, setBusy] = useState(false);

    const refresh = () => {
        getSitePrinter().then(setPrinter).catch(() => setPrinter(null));
        setHelper(undefined);
        helperStatus().then(setHelper);
    };

    useEffect(() => {
        if (open) {
            setFound(null);
            refresh();
        }
    }, [open]);

    const scan = () => {
        setScanning(true);
        setFound(null);
        scanForPrinters()
            .then(d => { setFound(d.printers || []); setNetworks(d.networks || []); })
            .catch(e => toast.error("Scan failed: " + e.message, {position: "top-right"}))
            .finally(() => setScanning(false));
    };

    const use = (p) => {
        setBusy(true);
        setSitePrinter({ip: p.ip, name: p.name || ("Zebra " + p.ip), model: p.model || ""})
            .then(saved => {
                setPrinter(saved);
                toast.success("Label printer set for all PCs: " + saved.name, {position: "top-right"});
            })
            .catch(err => toast.error((err.response && err.response.data && err.response.data.detail) || "Could not save the printer", {position: "top-right"}))
            .finally(() => setBusy(false));
    };

    const test = () => {
        setBusy(true);
        testPrint(printer.ip)
            .then(() => toast.success("Test label sent", {position: "top-right"}))
            .catch(e => toast.error("Test print failed: " + e.message, {position: "top-right"}))
            .finally(() => setBusy(false));
    };

    return (
        <Modal onClose={() => setOpen(false)} active={open}>
            <Modal.Content>
                <Section style={{background: "white"}}>
                    <Title>Label printer</Title>

                    <Box>
                        <p><strong>Site printer:</strong>{" "}
                            {printer && printer.ip
                                ? <>{printer.name} ({printer.ip}){printer.model ? " - " + printer.model : ""}</>
                                : <span className="has-text-grey">not set up yet</span>}
                        </p>
                        {printer && printer.updated_by ?
                            <p className="help">Set by {printer.updated_by}{printer.updated_at ? " on " + new Date(printer.updated_at).toLocaleString() : ""}. Every PC on site prints to this printer.</p>
                            : null}
                        <p style={{marginTop: "8px"}}><strong>This PC:</strong>{" "}
                            {helper === undefined ? "checking..." :
                                helper ? "ACS Label Printer Helper running (v" + helper.version + ")" :
                                    <span className="has-text-danger">ACS Label Printer Helper not running</span>}
                        </p>
                        {helper === null ?
                            <p className="help">
                                Each PC that prints labels needs the helper installed once -
                                see <a href="/labelprinter">Parts &gt; Label Printer</a> for step-by-step instructions
                                (or <a href={HELPER_DOWNLOAD} download>download it directly</a>).
                                If it is installed, check the browser hasn't blocked it - if Chrome or Edge asked to
                                "access devices on your local network", choose Allow.
                            </p> : null}
                    </Box>

                    <Columns>
                        <Column>
                            <Button color="primary" isFullWidth disabled={!helper || scanning} onClick={scan}>
                                {scanning ? "Scanning the network..." : "Auto-scan for Zebra printers"}
                            </Button>
                        </Column>
                        <Column>
                            <Button color="warning" isFullWidth disabled={!helper || !printer || !printer.ip || busy} onClick={test}>
                                Print test label
                            </Button>
                        </Column>
                    </Columns>

                    {found !== null ?
                        (found.length ?
                            <table className="table is-fullwidth is-striped is-narrow">
                                <thead><tr><th>Printer</th><th>Address</th><th>Model</th><th></th></tr></thead>
                                <tbody>
                                    {found.map(p =>
                                        <tr key={p.ip}>
                                            <td>{p.name}</td>
                                            <td>{p.ip}</td>
                                            <td>{p.model}</td>
                                            <td>
                                                {printer && printer.ip === p.ip ? "In use" :
                                                    <Button className="is-small" color="success" disabled={busy} onClick={() => use(p)}>Use this printer</Button>}
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table> :
                            <p className="notification is-warning is-light">
                                No Zebra printers found on {networks.join(", ") || "this PC's network"}. Check the printer is
                                switched on and connected, or enter its address below (print a configuration label
                                from the printer to see its IP).
                            </p>)
                        : null}

                    <label className="label" style={{marginTop: "12px"}}>Or enter the printer's IP address</label>
                    <Columns>
                        <Column size={8}>
                            <Input value={manualIp} placeholder="e.g. 192.168.1.50" onChange={e => setManualIp(e.target.value.trim())}/>
                        </Column>
                        <Column size={4}>
                            <Button isFullWidth disabled={!manualIp || busy} onClick={() => use({ip: manualIp})}>Use this address</Button>
                        </Column>
                    </Columns>

                    <Button color="info" isFullWidth onClick={() => setOpen(false)}>Close</Button>
                </Section>
            </Modal.Content>
        </Modal>
    );
}
